// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dialog component module for editing existing customers in the Hardfin system.
 * Provides a form-based interface with validation, error handling, and change tracking.
 */
import * as Sentry from '@sentry/react';
import * as z from 'zod';
import { ERROR_CODE_MAPPING, ERROR_MESSAGES } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { InfoOutlined } from '@/constants/hardfin-standard-icons';
import { Customer } from '@/customer/api/types';
import {
  UpdateCustomerParameters,
  updateCustomer,
} from '@/customer/api/update-customer';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseOneAddress } from 'email-addresses';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

/**
 * Validates if a string is a properly formatted email address.
 * Uses the email-addresses library for RFC-compliant validation.
 *
 * @param input - The string to validate as an email address
 * @returns true if the input is a valid email address, false otherwise
 */
function isEmailAddress(input: string) {
  const parsed = parseOneAddress({ input, rejectTLD: true });
  return parsed !== null;
}

/**
 * Schema for validating email addresses.
 * Allows empty strings (which will be converted to null) or valid email addresses.
 */
const emailSchema = z
  .string()
  .refine((input) => input === '' || isEmailAddress(input), {
    message: 'Invalid email address',
  });

/**
 * Schema for validating the company edit form.
 * Validates required fields, role flags, and proper formatting of contact information.
 */
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  domain: z.string().nullable(),
  billingAddress: z.string().nullable(),
  billingContact: z.object({
    name: z.string().nullable(),
    email: emailSchema.nullable(),
    phone: z.string().nullable(),
  }),
});

/**
 * Type definition for the form data structure.
 * Matches the structure defined in the form schema.
 */
type FormData = z.infer<typeof formSchema>;

/**
 * Props for the EditCustomerDialog component
 */
interface Props {
  /**
   * Controls the visibility of the dialog.
   * When true, the dialog is displayed.
   */
  isOpen: boolean;

  /**
   * The customer data to edit.
   * Used to populate the initial form values.
   */
  customer: Customer;

  /**
   * Callback fired when the dialog should be closed.
   * Triggered by clicking cancel, successful update, or dialog dismissal.
   */
  onClose: () => void;
}

/**
 * A dialog component for editing existing customer information with validation and change tracking.
 *
 * Features:
 * - Form validation using Zod schema
 * - Real-time error feedback
 * - Change tracking to only update modified fields
 * - Email format validation
 * - Support for error notifications
 * - Automatic query cache invalidation on success
 * - Error reporting to Sentry
 *
 * Form States:
 * 1. Initial: Pre-populated with current customer data
 * 2. Error: Displays validation errors or API errors
 * 3. Loading: During customer update
 * 4. Success: Automatically closes dialog
 *
 * @example
 * ```tsx
 * <EditCustomerDialog
 *   isOpen={showDialog}
 *   customer={customerData}
 *   onClose={() => setShowDialog(false)}
 * />
 * ```
 */
export function EditCustomerDialog(props: Props) {
  // Access the React Query client for cache management
  const queryClient = useQueryClient();

  /**
   * Mutation hook for updating customer information.
   * Handles success by invalidating queries and resetting form.
   * Reports errors to Sentry for monitoring.
   */
  const updateCustomerMutation = useMutation({
    mutationFn: (parameters: UpdateCustomerParameters) =>
      updateCustomer(props.customer.key, parameters),
    onError: (error) => {
      if (
        handleAPIError(error).apiStatusCode ===
        ERROR_CODE_MAPPING.customer_invalid_billing_contact_email
      ) {
        setError('billingContact.email', {
          type: 'custom',
          message: ERROR_MESSAGES.customer_invalid_billing_contact_email,
        });
        return;
      }

      Sentry.captureException(error);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      reset({
        name: data.customer.name,
        isCustomer: data.customer.isCustomer,
        isSupplier: data.customer.isSupplier,
        domain: data.customer.domain,
        billingAddress: data.customer.billingAddress,
        billingContact: {
          name: data.customer.billingContact?.name ?? null,
          email: data.customer.billingContact?.email ?? null,
          phone: data.customer.billingContact?.phone ?? null,
        },
      });
      close();
    },
  });

  /**
   * Form hook setup with Zod validation and initial values from customer data.
   * Tracks field changes to enable selective updates.
   */
  const { control, reset, handleSubmit, formState, watch, setValue, setError } =
    useForm<FormData>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        name: props.customer.name,
        isCustomer: props.customer.isCustomer,
        isSupplier: props.customer.isSupplier,
        domain: props.customer.domain,
        billingAddress: props.customer.billingAddress,
        billingContact: {
          name: props.customer.billingContact?.name ?? null,
          email: props.customer.billingContact?.email ?? null,
          phone: props.customer.billingContact?.phone ?? null,
        },
      },
    });

  // Watch role flag values for validation
  const isCustomer = watch('isCustomer');
  const isSupplier = watch('isSupplier');
  const hasNoRoles = !isCustomer && !isSupplier;

  // Reset form when customer data changes (e.g., after billing details update)
  useEffect(() => {
    reset({
      name: props.customer.name,
      isCustomer: props.customer.isCustomer,
      isSupplier: props.customer.isSupplier,
      domain: props.customer.domain,
      billingAddress: props.customer.billingAddress,
      billingContact: {
        name: props.customer.billingContact?.name ?? null,
        email: props.customer.billingContact?.email ?? null,
        phone: props.customer.billingContact?.phone ?? null,
      },
    });
  }, [props.customer, reset]);

  // Disable submit button if no fields have been modified
  const isSubmitDisabled = !formState.isDirty;

  /**
   * Handles dialog closure by calling onClose prop and resetting form state.
   */
  const close = () => {
    props.onClose();
    reset();
  };

  /**
   * Handles valid form submissions by preparing and sending update parameters.
   * Only includes changed fields in the update request.
   * Converts empty strings to null for optional fields.
   */
  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    // Only map parameters that have changed
    const changedParameters: UpdateCustomerParameters = {};

    if (formState.dirtyFields.name) {
      changedParameters.name = data.name;
    }

    if (formState.dirtyFields.isCustomer) {
      changedParameters.isCustomer = data.isCustomer;
    }

    if (formState.dirtyFields.isSupplier) {
      changedParameters.isSupplier = data.isSupplier;
    }

    if (formState.dirtyFields.domain) {
      // Treat empty string in the UI as null
      changedParameters.domain = data.domain === '' ? null : data.domain;
    }

    if (formState.dirtyFields.billingAddress) {
      changedParameters.billingAddress = data.billingAddress;
    }

    // Initialize billingContact object in parameters so linter will allow assignment
    changedParameters.billingContact = {};

    // Handle billing contact fields
    if (formState.dirtyFields.billingContact?.name) {
      changedParameters.billingContact.name = data.billingContact.name;

      // Treat empty string in the UI as null
      if (changedParameters.billingContact.name === '') {
        changedParameters.billingContact.name = null;
      }
    }

    if (formState.dirtyFields.billingContact?.email) {
      changedParameters.billingContact.email = data.billingContact.email;

      // Treat empty string in the UI as null
      if (changedParameters.billingContact.email === '') {
        changedParameters.billingContact.email = null;
      }
    }

    if (formState.dirtyFields.billingContact?.phone) {
      changedParameters.billingContact.phone = data.billingContact.phone;

      // Treat empty string in the UI as null
      if (changedParameters.billingContact.phone === '') {
        changedParameters.billingContact.phone = null;
      }
    }

    // Only update if there are actual changes
    if (
      Object.keys(changedParameters).length > 1 ||
      Object.keys(changedParameters.billingContact).length > 0
    ) {
      updateCustomerMutation.mutate(changedParameters);
    } else {
      close();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      {/* Error notification for API errors */}
      <SupportSnackbar
        isOpen={
          updateCustomerMutation.isError &&
          handleAPIError(updateCustomerMutation.error).apiStatusCode !==
            ERROR_CODE_MAPPING.customer_invalid_billing_contact_email
        }
        message="An error occurred while updating the company."
        onClose={updateCustomerMutation.reset}
      />

      <DialogTitle>Edit company</DialogTitle>

      {/* Form content */}
      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Basic Information */}
          <Stack spacing={2}>
            <Typography variant="subtitle2">Basic information</Typography>
            <FormTextField
              fullWidth
              name="name"
              label="Name"
              control={control}
            />
            <FormTextField
              fullWidth
              name="domain"
              label="Domain"
              placeholder="example.com"
              control={control}
            />
          </Stack>

          {/* Company Roles */}
          <Stack spacing={1}>
            <Typography variant="subtitle2">Company roles</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isCustomer}
                    onChange={(e) =>
                      setValue('isCustomer', e.target.checked, {
                        shouldDirty: true,
                      })
                    }
                  />
                }
                label="Customer"
              />
              <Tooltip
                arrow
                placement="right"
                title="Companies with the customer role appear automatically in project and fulfillment menus"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isSupplier}
                    onChange={(e) =>
                      setValue('isSupplier', e.target.checked, {
                        shouldDirty: true,
                      })
                    }
                  />
                }
                label="Supplier"
              />
              <Tooltip
                arrow
                placement="right"
                title="Companies with the supplier role appear automatically in procurement menus"
              >
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
            {hasNoRoles && (
              <Alert severity="warning">
                It is recommended to select at least one role. Companies without
                a role have limited functionality.
              </Alert>
            )}
          </Stack>

          {/* Billing Details Section */}
          <Stack spacing={1}>
            <Typography variant="subtitle2">Billing details</Typography>
            <Paper variant="outlined" sx={{ padding: 2 }}>
              <Stack spacing={2}>
                <Typography variant="body2">Billing address</Typography>
                <FormTextField
                  fullWidth
                  isMultiline
                  minimumRows={3}
                  name="billingAddress"
                  label="Billing address"
                  control={control}
                />
                <Stack spacing={2}>
                  <Typography variant="body2">Billing contact</Typography>
                  <FormTextField
                    fullWidth
                    name="billingContact.name"
                    label="Contact name"
                    control={control}
                  />
                  <FormTextField
                    fullWidth
                    name="billingContact.email"
                    label="Contact email"
                    control={control}
                  />
                  <FormTextField
                    fullWidth
                    name="billingContact.phone"
                    label="Contact phone"
                    control={control}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </DialogContent>

      {/* Dialog actions */}
      <DialogActions>
        <Button size="small" onClick={close}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          color="secondary"
          variant="contained"
          disabled={isSubmitDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
